import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  useColorModeValue,
  Icon,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Avatar,
  ButtonGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/contexts/ApiContext';

// Icons for team sections
const MyTeamsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

const AllTeamsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6c0-1.1.9-2 2-2s2 .9 2 2v6h8v-7.5c0-.83-.67-1.5-1.5-1.5S13 9.67 13 10.5V11H9V8.5C9 7.12 10.12 6 11.5 6S14 7.12 14 8.5V9h1v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V9c0-2.21-1.79-4-4-4-1.11 0-2.11.45-2.83 1.17C10.45 5.89 9.45 5.44 8.34 5.44 6.13 5.44 4.34 7.23 4.34 9.44V18z"
    />
  </Icon>
);

const RequestsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  </Icon>
);

const CreateTeamIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
    />
  </Icon>
);

interface TeamRequest {
  id: string;
  user: {
    identity: { user_id: string };
    display_name?: string;
    username?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  team: {
    id: string;
    name: string;
  };
  message?: string;
  status: string;
  created_at: string;
  requested_role?: string;
  role_id?: string;
}

export const TeamRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { TeamApi } = useApi();

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // State
  const [membershipRequests, setMembershipRequests] = useState<TeamRequest[]>([]);
  const [roleRequests, setRoleRequests] = useState<TeamRequest[]>([]);
  const [ownershipRequests, setOwnershipRequests] = useState<TeamRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Load all team requests for current user
  const loadRequests = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Note: This is a simplified implementation
      // In real implementation, you would need to get user's teams first
      // and then fetch requests for those teams
      
      // For now, return empty arrays to avoid API errors
      setMembershipRequests([]);
      setRoleRequests([]);
      setOwnershipRequests([]);
      
      // TODO: Implement actual API calls when backend supports it
      // const userTeams = await TeamApi.listTeams(undefined, undefined, undefined, 'managed');
      // for each team, get requests...
      
    } catch (err: any) {
      console.error('Error loading team requests:', err);
      setError(err.message || t('teams.failedToLoadRequests'));
      toast({
        title: t('common.error'),
        description: err.message || t('teams.failedToLoadRequests'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle approve request
  const handleApproveRequest = async (request: TeamRequest, type: string) => {
    try {
      // TODO: Implement actual approval logic
      toast({
        title: t('teams.requestApproved'),
        status: 'success',
      });
      loadRequests(); // Reload data
    } catch (err: any) {
      toast({
        title: t('common.error'),
        description: err.message,
        status: 'error',
      });
    }
  };

  // Handle reject request
  const handleRejectRequest = async (request: TeamRequest, type: string) => {
    try {
      // TODO: Implement actual rejection logic
      toast({
        title: t('teams.requestRejected'),
        status: 'info',
      });
      loadRequests(); // Reload data
    } catch (err: any) {
      toast({
        title: t('common.error'),
        description: err.message,
        status: 'error',
      });
    }
  };

  // Get user display name
  const getUserDisplayName = (user: TeamRequest['user']) => {
    if (user.display_name) return user.display_name;
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    return user.username || 'Unknown User';
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'yellow';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Update sidebars with context actions and subsections
  useEffect(() => {
    // Update right sidebar with actions
    if (window.updateRightSidebar) {
      window.updateRightSidebar([
        {
          label: t('teams.approveAll'),
          colorScheme: 'green',
          onClick: () => {
            // Approve all pending requests logic
            toast({
              title: t('common.info'),
              description: 'Approve all functionality will be implemented',
              status: 'info',
            });
          },
        },
        {
          label: t('teams.filterRequests'),
          colorScheme: 'blue',
          variant: 'outline',
          onClick: () => {
            // Filter requests logic
            toast({
              title: t('common.info'),
              description: 'Filter functionality will be implemented',
              status: 'info',
            });
          },
        },
      ]);
    }

    // Update left sidebar with subsections info for teams
    if (window.updateLeftSidebar) {
      const teamSections = [
        {
          path: '/teams/my-teams',
          label: t('teams.myTeams'),
          icon: MyTeamsIcon,
        },
        {
          path: '/teams/all-teams',
          label: t('teams.allTeams'),
          icon: AllTeamsIcon,
        },
        {
          path: '/teams/requests',
          label: t('teams.requests'),
          icon: RequestsIcon,
        },
        {
          path: '/teams/create',
          label: t('teams.createTeam'),
          icon: CreateTeamIcon,
        },
      ];

      window.updateLeftSidebar({
        section: 'teams',
        subsections: teamSections
      });
    }

    // Load requests data
    loadRequests();

    return () => {
      if (window.updateRightSidebar) {
        window.updateRightSidebar([]);
      }
      // Don't clear left sidebar subsections - let them persist for navigation
    };
  }, [t]);

  const totalRequests = membershipRequests.length + roleRequests.length + ownershipRequests.length;

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="7xl" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <HStack spacing={2}>
              <RequestsIcon />
              <Heading size="lg" color="text.primary">
                {t('teams.requests')} ({totalRequests})
              </Heading>
            </HStack>
            <Button
              size="sm"
              onClick={loadRequests}
              isLoading={isLoading}
              colorScheme="blue"
              variant="outline"
            >
              {t('common.refresh')}
            </Button>
          </HStack>

          {/* Loading state */}
          {isLoading && (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" />
              <Text>{t('teams.loadingRequests')}</Text>
            </VStack>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {/* Requests content */}
          {!isLoading && !error && (
            <>
              {totalRequests === 0 ? (
                <Box textAlign="center" py={12}>
                  <RequestsIcon />
                  <Text fontSize="lg" fontWeight="medium" mt={4} mb={2}>
                    {t('teams.noRequests')}
                  </Text>
                  <Text color={textColor}>
                    {t('teams.requestsWillAppearHere')}
                  </Text>
                </Box>
              ) : (
                <Tabs colorScheme="primary">
                  <TabList>
                    <Tab>
                      <HStack spacing={2}>
                        <Text>{t('teams.membershipRequests')}</Text>
                        <Badge colorScheme="blue" variant="solid" borderRadius="full">
                          {membershipRequests.length}
                        </Badge>
                      </HStack>
                    </Tab>
                    <Tab>
                      <HStack spacing={2}>
                        <Text>{t('teams.roleRequests')}</Text>
                        <Badge colorScheme="purple" variant="solid" borderRadius="full">
                          {roleRequests.length}
                        </Badge>
                      </HStack>
                    </Tab>
                    <Tab>
                      <HStack spacing={2}>
                        <Text>{t('teams.ownershipRequests')}</Text>
                        <Badge colorScheme="orange" variant="solid" borderRadius="full">
                          {ownershipRequests.length}
                        </Badge>
                      </HStack>
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Membership Requests */}
                    <TabPanel p={0} pt={4}>
                      {membershipRequests.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Text color={textColor}>{t('teams.noMembershipRequests')}</Text>
                        </Box>
                      ) : (
                        <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                          <CardBody p={0}>
                            <TableContainer>
                              <Table variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th>{t('teams.user')}</Th>
                                    <Th>{t('teams.team')}</Th>
                                    <Th>{t('teams.message')}</Th>
                                    <Th>{t('teams.status')}</Th>
                                    <Th>{t('teams.date')}</Th>
                                    <Th>{t('teams.actions')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {membershipRequests.map((request) => (
                                    <Tr key={request.id}>
                                      <Td>
                                        <HStack spacing={3}>
                                          <Avatar
                                            size="sm"
                                            name={getUserDisplayName(request.user)}
                                            src={request.user?.avatar_url}
                                          />
                                          <VStack align="start" spacing={0}>
                                            <Text fontSize="sm" fontWeight="medium">
                                              {getUserDisplayName(request.user)}
                                            </Text>
                                            {request.user?.username && (
                                              <Text fontSize="xs" color={textColor}>
                                                @{request.user.username}
                                              </Text>
                                            )}
                                          </VStack>
                                        </HStack>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" fontWeight="medium">
                                          {request.team.name}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" noOfLines={2} color={textColor}>
                                          {request.message || '-'}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={getStatusBadgeColor(request.status)}
                                          variant="subtle"
                                        >
                                          {request.status}
                                        </Badge>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" color={textColor}>
                                          {new Date(request.created_at).toLocaleDateString()}
                                        </Text>
                                      </Td>
                                      <Td>
                                        {request.status === 'pending' && (
                                          <ButtonGroup size="sm" spacing={2}>
                                            <Button
                                              colorScheme="green"
                                              onClick={() => handleApproveRequest(request, 'membership')}
                                            >
                                              {t('teams.approve')}
                                            </Button>
                                            <Button
                                              colorScheme="red"
                                              variant="outline"
                                              onClick={() => handleRejectRequest(request, 'membership')}
                                            >
                                              {t('teams.reject')}
                                            </Button>
                                          </ButtonGroup>
                                        )}
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </CardBody>
                        </Card>
                      )}
                    </TabPanel>

                    {/* Role Requests */}
                    <TabPanel p={0} pt={4}>
                      {roleRequests.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Text color={textColor}>{t('teams.noRoleRequests')}</Text>
                        </Box>
                      ) : (
                        <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                          <CardBody p={0}>
                            <TableContainer>
                              <Table variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th>{t('teams.user')}</Th>
                                    <Th>{t('teams.team')}</Th>
                                    <Th>{t('teams.requestedRole')}</Th>
                                    <Th>{t('teams.message')}</Th>
                                    <Th>{t('teams.status')}</Th>
                                    <Th>{t('teams.actions')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {roleRequests.map((request) => (
                                    <Tr key={request.id}>
                                      <Td>
                                        <HStack spacing={3}>
                                          <Avatar
                                            size="sm"
                                            name={getUserDisplayName(request.user)}
                                            src={request.user?.avatar_url}
                                          />
                                          <VStack align="start" spacing={0}>
                                            <Text fontSize="sm" fontWeight="medium">
                                              {getUserDisplayName(request.user)}
                                            </Text>
                                            {request.user?.username && (
                                              <Text fontSize="xs" color={textColor}>
                                                @{request.user.username}
                                              </Text>
                                            )}
                                          </VStack>
                                        </HStack>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" fontWeight="medium">
                                          {request.team.name}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Badge colorScheme="blue" variant="outline">
                                          {request.requested_role || request.role_id}
                                        </Badge>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" noOfLines={2} color={textColor}>
                                          {request.message || '-'}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={getStatusBadgeColor(request.status)}
                                          variant="subtle"
                                        >
                                          {request.status}
                                        </Badge>
                                      </Td>
                                      <Td>
                                        {request.status === 'pending' && (
                                          <ButtonGroup size="sm" spacing={2}>
                                            <Button
                                              colorScheme="green"
                                              onClick={() => handleApproveRequest(request, 'role')}
                                            >
                                              {t('teams.approve')}
                                            </Button>
                                            <Button
                                              colorScheme="red"
                                              variant="outline"
                                              onClick={() => handleRejectRequest(request, 'role')}
                                            >
                                              {t('teams.reject')}
                                            </Button>
                                          </ButtonGroup>
                                        )}
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </CardBody>
                        </Card>
                      )}
                    </TabPanel>

                    {/* Ownership Requests */}
                    <TabPanel p={0} pt={4}>
                      {ownershipRequests.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Text color={textColor}>{t('teams.noOwnershipRequests')}</Text>
                        </Box>
                      ) : (
                        <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                          <CardBody p={0}>
                            <TableContainer>
                              <Table variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th>{t('teams.user')}</Th>
                                    <Th>{t('teams.team')}</Th>
                                    <Th>{t('teams.message')}</Th>
                                    <Th>{t('teams.status')}</Th>
                                    <Th>{t('teams.date')}</Th>
                                    <Th>{t('teams.actions')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {ownershipRequests.map((request) => (
                                    <Tr key={request.id}>
                                      <Td>
                                        <HStack spacing={3}>
                                          <Avatar
                                            size="sm"
                                            name={getUserDisplayName(request.user)}
                                            src={request.user?.avatar_url}
                                          />
                                          <VStack align="start" spacing={0}>
                                            <Text fontSize="sm" fontWeight="medium">
                                              {getUserDisplayName(request.user)}
                                            </Text>
                                            {request.user?.username && (
                                              <Text fontSize="xs" color={textColor}>
                                                @{request.user.username}
                                              </Text>
                                            )}
                                          </VStack>
                                        </HStack>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" fontWeight="medium">
                                          {request.team.name}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" noOfLines={2} color={textColor}>
                                          {request.message || '-'}
                                        </Text>
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={getStatusBadgeColor(request.status)}
                                          variant="subtle"
                                        >
                                          {request.status}
                                        </Badge>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm" color={textColor}>
                                          {new Date(request.created_at).toLocaleDateString()}
                                        </Text>
                                      </Td>
                                      <Td>
                                        {request.status === 'pending' && (
                                          <ButtonGroup size="sm" spacing={2}>
                                            <Button
                                              colorScheme="green"
                                              onClick={() => handleApproveRequest(request, 'ownership')}
                                            >
                                              {t('teams.approve')}
                                            </Button>
                                            <Button
                                              colorScheme="red"
                                              variant="outline"
                                              onClick={() => handleRejectRequest(request, 'ownership')}
                                            >
                                              {t('teams.reject')}
                                            </Button>
                                          </ButtonGroup>
                                        )}
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </CardBody>
                        </Card>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
}; 